import { Fragment, useCallback, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';

import type { NavItem } from '@shared/models/nav-item.model';
import { useNavigateTo } from '@shared/hooks/useReturnTo';
import { Icon } from '@ui/Icon';
import { Tooltip } from '@ui/Tooltip';

/**
 * The recursive left-hand navigation.
 *
 * The tree is rendered by `NavNode` calling itself once per level. The
 * `data-level` and `--row` hooks it emits are what `App.scss` styles the
 * indentation and row sizing through.
 *
 * Expansion state lives here rather than in the shell because nothing outside
 * the nav reads it.
 */

export interface SidebarNavProps {
  items: NavItem[];
  collapsed: boolean;
  /** Clears the open activity when a leaf is chosen. */
  onLeafClick: () => void;
}

/** Whether `route` is the active one; `exact` requires a full path match. */
function isRouteActive(pathname: string, route: string | undefined, exact: boolean): boolean {
  if (!route) {
    return false;
  }

  if (exact) {
    return pathname === route;
  }

  return pathname === route || pathname.startsWith(`${route}/`);
}

function routesOf(items: NavItem[]): string[] {
  return items.flatMap((item) => [...(item.route ? [item.route.split('?')[0]] : []), ...routesOf(item.children ?? [])]);
}

/**
 * The nav route the current page belongs to: the longest one it sits under.
 *
 * Today, Batches and Ask are top-level rows whose routes live under
 * `/assignments`, so a plain prefix match lit up the Assignments branch beside
 * them. Taking the longest match gives each page exactly one owner.
 */
function owningRoute(pathname: string, routes: string[]): string | null {
  return routes
    .filter((route) => route !== '/home' || pathname === '/home')
    .filter((route) => isRouteActive(pathname, route, false))
    .reduce<string | null>((best, route) => (best && best.length >= route.length ? best : route), null);
}

function hasActiveRoute(item: NavItem, owner: string | null): boolean {
  return owner !== null && routesOf([item]).includes(owner);
}

export function SidebarNav({ items, collapsed, onLeafClick }: SidebarNavProps) {
  /*
   * Keyed by `title-level`.
   *
   * An entry is only written once a branch has been clicked. Until then
   * `isExpanded` falls back to "is one of my routes active", which is what
   * makes a branch open itself when you land inside it from a deep link.
   */
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});

  const { pathname } = useLocation();
  const owner = owningRoute(pathname, routesOf(items));

  const toggleItem = useCallback((item: NavItem, level: number) => {
    setExpandedStates((current) => {
      const key = `${item.title}-${level}`;
      return { ...current, [key]: !current[key] };
    });
  }, []);

  return (
    <div>
      {items.map((item, index) => (
        <Fragment key={item.title}>
          {/* Collapsed to a rail a heading has no room, so a hairline marks the break. */}
          {item.section && item.section !== items[index - 1]?.section && (
            <div className="sidebar-group-label" role="presentation">
              {collapsed ? null : item.section}
            </div>
          )}
          <NavNode
            item={item}
            owner={owner}
            level={0}
            row={index}
            collapsed={collapsed}
            expandedStates={expandedStates}
            onToggle={toggleItem}
            onLeafClick={onLeafClick}
          />
        </Fragment>
      ))}
    </div>
  );
}

interface NavNodeProps {
  item: NavItem;
  /** The route the current page belongs to, from `owningRoute`. */
  owner: string | null;
  level: number;
  row?: number;
  collapsed: boolean;
  expandedStates: Record<string, boolean>;
  onToggle: (item: NavItem, level: number) => void;
  onLeafClick: () => void;
}

function NavNode({ item, owner, level, row, collapsed, expandedStates, onToggle, onLeafClick }: NavNodeProps) {
  // Re-clicking the row for the page on screen must not stack a duplicate entry.
  const navigate = useNavigateTo();
  const { pathname } = useLocation();

  const key = `${item.title}-${level}`;
  const active = hasActiveRoute(item, owner);
  const expanded = expandedStates[key] ?? active;

  // `--row` staggers the sublist reveal; it is read by app.component.scss.
  const style = { '--row': row } as CSSProperties;

  /* ------------------------------------------------------------------ leaf -- */
  if (!item.children) {
    return (
      <div className="sidebar-node" data-level={level} style={style}>
        <Tooltip message={collapsed ? item.title : ''} position="right" showDelay={350}>
          <button
            type="button"
            className={[
              'sidebar-item',
              level > 0 ? 'sidebar-item--child' : null,
              'sidebar-item--leaf',
              (level === 0 ? owner === item.route : isRouteActive(pathname, item.route, true)) ? 'active' : null
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              onLeafClick();

              if (item.route) {
                navigate(item.route);
              }
            }}
          >
            <div className="sidebar-item__prefix">
              <Icon>{item.iconImage}</Icon>
            </div>

            {(!collapsed || level > 0) && (
              <span className="sidebar-item__label" title={item.title}>
                {item.title}
              </span>
            )}

            {item.badgeCount ? <span className="sidebar-item__badge">{item.badgeCount}</span> : null}
          </button>
        </Tooltip>
      </div>
    );
  }

  /* ---------------------------------------------------------------- branch -- */
  return (
    <div className="sidebar-node" data-level={level} style={style}>
      <div className="sidebar-branch" data-level={level}>
        <Tooltip message={collapsed ? item.title : ''} position="right" showDelay={350}>
          <button
            type="button"
            className={[
              'sidebar-item',
              level > 0 ? 'sidebar-item--child' : null,
              expanded ? 'expanded' : null,
              active ? 'active' : null
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              // Collapsed to a rail there is no room to reveal children, so the
              // branch behaves as a link to its own landing route instead.
              if (collapsed && item.route) {
                navigate(item.route);
                return;
              }

              onToggle(item, level);
            }}
          >
            <div className="sidebar-item__prefix">
              <Icon>{item.iconImage}</Icon>
            </div>

            {(!collapsed || level > 0) && <span className="sidebar-item__label">{item.title}</span>}

            {(!collapsed || level > 0) && <Icon className="sidebar-item__chevron">chevron_right</Icon>}
          </button>
        </Tooltip>

        {/*
          `inert` as well as aria-hidden: a collapsed sublist was still in the
          tab order, so tabbing through the nav walked into rows nobody could
          see. Same pairing the activity sections use.
        */}
        <div
          className={['sidebar-sublist', expanded ? 'expanded' : null].filter(Boolean).join(' ')}
          aria-hidden={!expanded}
          inert={!expanded}
        >
          <div className="sidebar-sublist__inner">
            {item.children.map((child, index) => (
              <NavNode
                key={child.title}
                item={child}
                owner={owner}
                level={level + 1}
                row={index}
                collapsed={collapsed}
                expandedStates={expandedStates}
                onToggle={onToggle}
                onLeafClick={onLeafClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SidebarNav;
